import { Box, StackDivider, TableContainer, useColorModeValue, VStack } from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useStableCallback } from '@main/shared/browser';
import { ColumnSizingState, Header, Table } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useCallback, useMemo, useRef } from 'react';

import { shouldDisplayPagination, TablePagination } from './pagination';
import { TableItem, type TableRowClickHandler, TableRowReorderHandler } from './table';
import { TableActionHandler } from './table-action';
import { TableColumnHeader } from './table-column-header';
import { TableContext } from './table-context';
import { columnTrack } from './table-grid';
import { TableRow } from './table-row';
import { useTableOverflow } from './use-table-overflow';

export interface TableFilterResultProps<T> {
  itemName: TableItem;
  table: Table<T>;
  minColumnSize: number;
  actionHandler: TableActionHandler<T>;
  onRowClick?: TableRowClickHandler<T>;
  setColumnSizing: Dispatch<SetStateAction<ColumnSizingState>>;
  onRowReorder?: TableRowReorderHandler<T>;
  pageSizeOptions: [number, ...number[]];
  isBulkSelectionToolbarRenderedInTable: boolean;
  hasNextPage?: boolean;
}

export function TableFilterResult<T>({
  itemName,
  table,
  minColumnSize,
  actionHandler,
  onRowClick,
  setColumnSizing,
  onRowReorder,
  pageSizeOptions,
  isBulkSelectionToolbarRenderedInTable,
  hasNextPage,
}: TableFilterResultProps<T>) {
  const theadColor = useColorModeValue('gray.500', 'gray.400');
  const bgColor = useColorModeValue(`gray.25`, `gray.700`);
  const cardBgColor = useColorModeValue(`white`, `gray.800`);
  const borderColor = useColorModeValue(`gray.200`, `gray.600`);
  const resizeHandleColor = useColorModeValue('blue.500', 'blue.300');
  const rowHoverBackgroundColor = useColorModeValue('blue.25', '#202533');
  const headers = table.getFlatHeaders();
  const rows = table.getRowModel().rows;
  const rowSelection = table.getState().rowSelection;
  const columnSizingInfo = table.getState().columnSizingInfo;
  const columnSizing = table.getState().columnSizing;
  const isReorderable = !!onRowReorder && rows.length > 1;
  const rowStyles = useMemo(
    () => ({
      position: 'relative',
      display: 'grid',
      gridTemplateColumns: 'subgrid',
      gridColumn: `1/end`,
      '+ .why-table--row': {
        borderTop: '1px',
        borderTopColor: `${borderColor} !important`,
      },
      '&.why-table--row-header': {
        borderBottom: '1px',
        borderBottomColor: `${borderColor} !important`,
        '&, & [role=columnheader]': {
          backgroundColor: bgColor,
        },
      },
      ':after': {
        content: '""',
        position: 'absolute',
        display: 'none',
        pointerEvents: 'none',
        inset: '0',
        border: '2px',
        borderColor: 'blue.200',
        zIndex: 1,
      },
      '&.why-table--row-active:after': {
        display: 'block',
      },
      '&:not(.why-table--row-header)': {
        '&, & [role=cell]': {
          backgroundColor: cardBgColor,
        },
        '&:hover': {
          '&, & [role=cell]': {
            backgroundColor: rowHoverBackgroundColor,
          },
        },
      },
      '.without-pagination &:last-of-type': {
        '&:not(.why-table--row-header), &:after': {
          borderBottomLeftRadius: 'var(--card-radius)',
          borderBottomRightRadius: 'var(--card-radius)',
        },
      },
    }),
    [bgColor, borderColor, cardBgColor, rowHoverBackgroundColor],
  );

  const theadStyles = useMemo(
    () => ({
      position: 'relative',
      transition: 'background-color 0.2s ease-out',
      color: theadColor,
      fontWeight: 'medium',
      fontSize: 'xs',
      lineHeight: '4',
      textTransform: 'none',
      letterSpacing: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      px: 2,
      py: 3,
      '&:hover .why-table--resize-handle': {
        backgroundColor: resizeHandleColor,
      },
    }),
    [resizeHandleColor, theadColor],
  );

  const tableRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useTableOverflow(tableRef, table);

  const tableContextValue = useMemo(
    () => ({
      isOverflowing,
    }),
    [isOverflowing],
  );

  const headerToColumn = useCallback(
    (header: Header<T, unknown>) =>
      columnTrack(header, { columnSizing, minColumnSize, isReorderable }),
    [columnSizing, minColumnSize, isReorderable],
  );

  const gridTemplateColumns = useMemo(
    () => headers.map(headerToColumn).join(' '),
    [headers, headerToColumn],
  );

  const headerRefs = useRef<Record<string, HTMLElement>>({});
  const getHeaderRef = useStableCallback((id: string) => {
    return (element: HTMLElement | null) => {
      if (element) {
        headerRefs.current[id] = element;
      } else {
        delete headerRefs.current[id];
      }
    };
  });

  const updateColumnSizing = useStableCallback(async () => {
    setColumnSizing(
      Object.fromEntries(
        Object.entries(headerRefs.current)
          .filter(([id]) => table.getColumn(id)?.getCanResize())
          .map(([id, el]) => [id, el.getBoundingClientRect().width]),
      ),
    );
  });

  const handleDragEnd = useStableCallback((event: DragEndEvent) => {
    if (!onRowReorder || !event.over || event.active.id === event.over.id) {
      return;
    }

    const fromRow = table.getRow(event.active.id as string);
    const toRow = table.getRow(event.over.id as string);

    onRowReorder(fromRow, toRow, table);
  });

  const renderHeader = useCallback(
    (header: Header<T, unknown>) => {
      return (
        <TableColumnHeader
          key={header.id}
          ref={getHeaderRef(header.id)}
          header={header}
          resizeHandleColor={resizeHandleColor}
          styles={theadStyles}
          updateColumnSizing={updateColumnSizing}
          isReorderable={isReorderable}
        />
      );
    },
    [getHeaderRef, resizeHandleColor, theadStyles, updateColumnSizing, isReorderable],
  );

  const tHead = (
    <Box
      role="row"
      className="why-table--row why-table--row-header"
      userSelect={columnSizingInfo.isResizingColumn ? 'none' : undefined}
      __css={rowStyles}
    >
      {headers.map(renderHeader)}
    </Box>
  );

  const onRowClickStable: TableRowClickHandler<T> = useStableCallback(onRowClick);
  onRowClickStable.isSupported = onRowClick?.isSupported;
  const actionHandlerCb = useStableCallback(actionHandler);
  const tBody = useMemo(
    () =>
      rows.map((row) => (
        <TableRow
          key={row.id}
          row={row}
          itemName={itemName}
          rowStyles={rowStyles}
          isReorderable={isReorderable}
          actionHandler={actionHandlerCb}
          onRowClick={onRowClick ? onRowClickStable : undefined}
        />
      )),
    // HACK: Need `headers` and `rowSelection` to re-render when columns or row selection change
    // oxlint-disable-next-line react/exhaustive-deps
    [
      rows,
      itemName,
      rowStyles,
      isReorderable,
      onRowClick,
      onRowClickStable,
      actionHandlerCb,
      headers,
      rowSelection,
    ],
  );

  const isPaginationEnabled = shouldDisplayPagination(table, pageSizeOptions, hasNextPage);

  return (
    <TableContext.Provider value={tableContextValue}>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        sensors={useSensors(
          useSensor(MouseSensor),
          useSensor(TouchSensor),
          useSensor(KeyboardSensor),
        )}
      >
        <VStack align="normal" spacing={0} divider={<StackDivider />}>
          <TableContainer
            // Trap sticky column z-index so pinned actions stay above table cells only,
            // not filter/sort menus and other UI outside the scroll container.
            isolation="isolate"
            borderBottomRadius={
              isPaginationEnabled || isBulkSelectionToolbarRenderedInTable ? undefined : 'lg'
            }
          >
            <Box
              role="table"
              aria-label={itemName.plural}
              display="grid"
              gridTemplateColumns={gridTemplateColumns}
              placeItems="stretch"
              w="full"
              ref={tableRef}
            >
              <Box display="contents" role="rowgroup">
                {tHead}
              </Box>
              <Box display="contents" role="rowgroup">
                <SortableContext items={rows} strategy={verticalListSortingStrategy}>
                  {tBody}
                </SortableContext>
              </Box>
            </Box>
          </TableContainer>
          {isPaginationEnabled && (
            <TablePagination
              table={table}
              pageSizeOptions={pageSizeOptions}
              hasNextPage={hasNextPage}
            />
          )}
        </VStack>
      </DndContext>
    </TableContext.Provider>
  );
}
